import { AlertTriangle } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { InstitutionalProfileFormData } from '@/types/institutionalProfile'
import type { EmailDomainClaimResult } from '@/types/organization'
import { CheckboxRow, SectionHeading } from './FormField'
import {
  INCORPORATION_COUNTRIES,
  QUALIFICATION_COPY,
  QUALIFICATION_FIELDS,
} from './institutionalFields'
import { BODY_TEXT, ERROR_TEXT, HELP_TEXT } from './styles'

interface ReviewStepProps {
  profile: InstitutionalProfileFormData
  claim: EmailDomainClaimResult | null
  onProfileChange: (name: keyof InstitutionalProfileFormData, value: string | boolean) => void
  errors: Record<string, string>
  disabled?: boolean
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5 border-b border-[rgba(45,41,35,0.08)] py-2 last:border-b-0 dark:border-[rgba(253,253,253,0.08)] sm:flex-row sm:items-baseline sm:gap-4">
    <span className={cn(HELP_TEXT, 'sm:w-[220px] sm:shrink-0')}>{label}</span>
    <span className="font-plus-jakarta text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
      {value || '—'}
    </span>
  </div>
)

/**
 * The last step of PHASE 2 — confirm and finish.
 *
 * 🔴 Nothing is submitted here. The attestation was submitted at the end of
 * phase 1 and is sitting in PENDING_VERIFICATION; everything on this step has
 * already been written by the step that owns it (the profile by autosave, the
 * domain by its claim call, the KYB by Sumsub). This is a summary and a
 * doorway back into the app, not a commit point.
 *
 * The domain warning still matters: an unverified domain holds the attestation
 * short of COMPLETE, so a user who is not told will read a stalled account as a
 * failure.
 */
export const ReviewStep = ({
  profile,
  claim,
  onProfileChange,
  errors,
  disabled,
}: ReviewStepProps) => {
  const qualifications = QUALIFICATION_FIELDS.filter(name => profile[name]).map(
    name => QUALIFICATION_COPY[name].label,
  )
  // 🔴 From the PROFILE, not from the attestation draft.
  //
  // This step only ever renders in phase 2, and phase 2 deliberately does not
  // load the attestation draft — the row was submitted at the end of phase 1
  // and `GET /attestations/draft` answers null for it. So the attestation's
  // `country` is always the empty default here, and reading it told every
  // phase-2 user that no country of incorporation was on file, in a summary
  // whose whole job is to reassure them their answers arrived.
  // `incorporation_country` is the same fact, hydrated from the organization,
  // and is an ISO 3166-1 alpha-3 code — so it is mapped back to its label.
  const incorporationCountry =
    INCORPORATION_COUNTRIES.find(option => option.value === profile.incorporation_country)
      ?.label ?? profile.incorporation_country
  const domainPending = !!claim?.email_domain && !claim.email_domain_verified

  return (
    <div className="space-y-8">
      <section>
        <SectionHeading title="Review" />
        <div>
          <Row label="Institution" value={profile.institution_name} />
          <Row label="Primary contact" value={profile.primary_contact_email} />
          <Row label="Country of incorporation" value={incorporationCountry} />
          <Row label="Qualification" value={qualifications.join(', ')} />
          <Row
            label="Email domain"
            value={
              claim?.email_domain
                ? `${claim.email_domain}${claim.email_domain_verified ? ' (verified)' : ' (pending verification)'}`
                : 'Not claimed'
            }
          />
        </div>
      </section>

      {domainPending && (
        <div className="flex items-start gap-3 border border-[#ff540c]/40 bg-[#ff540c]/10 px-4 py-3">
          <AlertTriangle size={20} className="mt-[2px] shrink-0 text-[#ff540c]" />
          <div>
            <p className="font-plus-jakarta text-[14px] font-medium text-[#2d2923] dark:text-[#fdfdfd]">
              Your domain is not verified yet
            </p>
            <p className={cn(HELP_TEXT, 'mt-0.5')}>
              Your account stays pending until the verification link at{' '}
              <strong>{claim?.email_domain}</strong> is opened. You can keep browsing SecondLane in
              the meantime — pricing and trading open up once verification completes.
            </p>
          </div>
        </div>
      )}

      <section>
        <SectionHeading title="Final confirmations" />
        <div className="space-y-3">
          <CheckboxRow
            id="terms_accepted"
            checked={profile.terms_accepted}
            onChange={checked => onProfileChange('terms_accepted', checked)}
            label="The entity accepts SecondLane's terms of service"
            error={errors.terms_accepted}
            disabled={disabled}
          />
          <CheckboxRow
            id="marketing_consent"
            checked={profile.marketing_consent}
            onChange={checked => onProfileChange('marketing_consent', checked)}
            label="SecondLane may contact the entity about products and services"
            help="Optional."
            disabled={disabled}
          />
        </div>
      </section>

      {errors.__submit && <p className={ERROR_TEXT}>{errors.__submit}</p>}

      <p className={BODY_TEXT}>
        Your attestation is already recorded against your account on behalf of the entity. Once the
        business verification comes back approved, pricing, valuations and trading open up.
      </p>
    </div>
  )
}
